import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ZodEffects, ZodError, ZodObject } from "zod";
import {
  getDashboardWidgetContract,
  validateDashboardWidgetParams,
  validateWidgetParams,
  WIDGET_LABELS,
} from "@playbookai/widget-schemas";
import { Button } from "../../atoms/Button/Button.js";
import { Input } from "../../atoms/Input/Input.js";
import { Modal } from "../../molecules/Modal/Modal.js";
import { EmptyState } from "../../molecules/EmptyState/index.js";
import { Alert } from "../../molecules/Alert/index.js";
import { Card, CardBody } from "../../molecules/Card/index.js";
import { getWidget } from "../../widgets/registry.js";
import { WidgetErrorBoundary, WidgetSlot } from "./WidgetErrorBoundary.js";
import { widgetLabel } from "./helpers.js";
import type { DashboardItem } from "./types.js";

export interface WidgetEditDialogProps {
  open: boolean;
  item: DashboardItem;
  /** Same body slot the dashboard uses — drives the live preview from the draft. */
  renderWidget: (item: DashboardItem) => ReactNode;
  onClose: () => void;
  onSave: (next: {
    params?: Record<string, unknown>;
    title?: string | undefined;
  }) => void;
}

/**
 * Try to validate `params` against the schema the SERVER will apply; return
 * issue messages on failure, or null when valid (both validators `.parse`, so
 * they throw).
 *
 * A dashboard instance is governed by the kind's DASHBOARD contract, whose
 * presentation-only params schema (plan §9.1) is deliberately narrower
 * than the same kind's legacy canvas schema — validating a dashboard draft
 * against the legacy schema let it pass here and still be rejected by the
 * server. Keys the dashboard schema does not declare belong to the widget's
 * data CONNECTION (a picked `kpiId`/`kpiIds`, §11.4) and are stripped by the
 * host before the instance PATCH, so they are the host's to validate, not
 * ours. A kind with no dashboard contract is a canvas/legacy widget and keeps
 * the legacy schema exactly as before.
 */
function validate(
  kind: DashboardItem["kind"],
  params: Record<string, unknown>,
): string[] | null {
  try {
    const contract = getDashboardWidgetContract(kind);
    if (contract) {
      validateDashboardWidgetParams(
        kind,
        presentationParams(contract.paramsSchema, params),
      );
    } else {
      validateWidgetParams(kind, params);
    }
    return null;
  } catch (e) {
    if (e instanceof ZodError) {
      return e.issues.map((i) => i.message);
    }
    return ["These settings are invalid."];
  }
}

/** The draft narrowed to the keys the dashboard params schema governs. */
function presentationParams(
  schema: unknown,
  params: Record<string, unknown>,
): Record<string, unknown> {
  let inner = schema;
  while (inner instanceof ZodEffects) inner = inner.innerType();
  if (!(inner instanceof ZodObject)) return params;
  const governed = Object.keys(inner.shape as Record<string, unknown>);
  return Object.fromEntries(
    Object.entries(params).filter(([key]) => governed.includes(key)),
  );
}

/**
 * Per-widget edit dialog (PB-177 PR-B). DS `Modal` with a form pane (optional
 * title + the kind's registered `EditorForm`) and a live preview pane rendered
 * through the dashboard's `renderWidget` slot. Validation is **reactive**:
 * `validateWidgetParams` runs on every change, surfaces issues, and disables
 * Save while invalid or unchanged. Data-blind — Save hands the draft back to the
 * host via `onSave`; the dialog persists nothing.
 */
export function WidgetEditDialog({
  open,
  item,
  renderWidget,
  onClose,
  onSave,
}: WidgetEditDialogProps) {
  const meta = getWidget(item.kind)?.dashboard;
  const EditorForm = meta?.EditorForm;
  const supportsTitle = !!meta?.supportsTitleOverride;

  const [draftParams, setDraftParams] = useState<Record<string, unknown>>(
    () => item.params ?? {},
  );
  const nameId = useId();
  const [editorIssue, setEditorIssue] = useState<string | null>(null);
  const [editSession, setEditSession] = useState(0);
  const [draftTitle, setDraftTitle] = useState(() => item.title ?? "");
  const [issues, setIssues] = useState<string[] | null>(() =>
    validate(item.kind, item.params ?? {}),
  );

  /* The host keeps this dialog mounted so it can play its exit (ADR-0091), so
     the draft is seeded on the closed -> open edge rather than only on mount —
     otherwise an edit abandoned with Cancel is still in the form the next time
     the same widget is opened, and Save would commit it. Not on every render:
     `item` changing under an open dialog is a background refetch, and wiping
     what the author typed is worse than showing a stale preview. And never on
     the closing edge — a dialog shows what it was showing for its whole exit. */
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setEditorIssue(null);
      setEditSession((session) => session + 1);
      setDraftParams(item.params ?? {});
      setDraftTitle(item.title ?? "");
      setIssues(validate(item.kind, item.params ?? {}));
    }
    wasOpen.current = open;
  }, [open, item.kind, item.params, item.title]);

  const onParamsChange = (next: Record<string, unknown>) => {
    setDraftParams(next);
    setIssues(validate(item.kind, next));
  };

  const trimmedTitle = draftTitle.trim();
  const dirty =
    (supportsTitle && (item.title ?? "") !== trimmedTitle) ||
    JSON.stringify(draftParams) !== JSON.stringify(item.params ?? {});
  const invalid = issues !== null || editorIssue !== null;

  const previewItem: DashboardItem = useMemo(
    () => ({
      instanceId: item.instanceId,
      kind: item.kind,
      width: item.width,
      params: draftParams,
      ...(supportsTitle && trimmedTitle ? { title: trimmedTitle } : {}),
    }),
    [
      item.instanceId,
      item.kind,
      item.width,
      draftParams,
      supportsTitle,
      trimmedTitle,
    ],
  );

  const commit = () => {
    if (invalid || validate(item.kind, draftParams) !== null) return; // defensive
    onSave({
      params: draftParams,
      ...(supportsTitle ? { title: trimmedTitle || undefined } : {}),
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="editor"
      placement="center"
      description="Choose what appears in this widget. Preview your changes before saving."
      title={
        <span>
          Edit{" "}
          <span className="text-pb-text-muted uppercase tracking-wider text-dense-1">
            {widgetLabel(item)}
          </span>
        </span>
      }
      footer={
        <>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!dirty || invalid}
            onClick={commit}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="grid min-h-0 grid-cols-1 gap-6 md:h-full md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="min-w-0 space-y-5 md:min-h-0 md:overflow-y-auto md:pe-2 md:[scrollbar-gutter:stable]">
          {EditorForm ? (
            <EditorForm
              key={editSession}
              draftParams={draftParams}
              onChange={onParamsChange}
              onValidityChange={setEditorIssue}
            />
          ) : !supportsTitle ? (
            <p className="text-sm text-pb-text-muted">
              This widget has no configurable settings.
            </p>
          ) : null}
          {supportsTitle && (
            <div className="border-t border-pb-border pt-4">
              <Input
                id={nameId}
                label="Widget name"
                value={draftTitle}
                maxLength={60}
                placeholder={WIDGET_LABELS[item.kind] ?? item.kind}
                onChange={(event) => setDraftTitle(event.target.value)}
              />
              <p className="mt-1 text-dense-1 text-pb-text-muted">
                Optional. Leave blank to use the default name.
              </p>
            </div>
          )}
          {editorIssue && (
            <Alert variant="inline" tone="critical">
              {editorIssue}
            </Alert>
          )}
          {issues && issues.length > 0 && (
            <ul
              className="text-dense-1 text-pb-red-text space-y-0.5"
              role="alert"
            >
              {issues.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="min-w-0 flex flex-col gap-2 md:min-h-0">
          <div className="text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold mb-2">
            Live preview
          </div>
          <Card
            variant="inset"
            className="flex flex-col min-h-72 md:min-h-0 md:flex-1 md:overflow-y-auto md:[scrollbar-gutter:stable]"
            {...{ inert: "" }}
          >
            <CardBody className="min-h-0 flex-1">
              {invalid ? (
                <EmptyState
                  size="sm"
                  title="Complete your selection"
                  description={
                    editorIssue ??
                    issues?.join(" ") ??
                    "Check your settings to see a preview."
                  }
                />
              ) : (
                <WidgetErrorBoundary
                  widgetName={widgetLabel(item)}
                  resetKeys={[item.instanceId, draftParams]}
                >
                  <WidgetSlot item={previewItem} renderWidget={renderWidget} />
                </WidgetErrorBoundary>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </Modal>
  );
}
